/**
 * TOSS Web Application - Guest Route
 *
 * Route wrapper that only allows access to non-authenticated users.
 * Redirects authenticated users to their default route.
 * Used for auth pages (login, register, forgot-password, etc.)
 *
 * This is the opposite of ProtectedRoute:
 * - ProtectedRoute: requires authentication, redirects guests to login
 * - GuestRoute: requires NO authentication, redirects authenticated users away
 */

import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store';
import { getDefaultRoute } from '@/constants';
import { PageLoading } from '@/components/ui';

// ============================================================================
// Types
// ============================================================================

export interface GuestRouteProps {
  children: React.ReactNode;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Route wrapper for guest-only pages (login, register, etc.)
 *
 * Prevents authenticated users from accessing auth pages and
 * ensures these pages are not added to browser history for logged-in users.
 *
 * @example
 * ```tsx
 * <GuestRoute>
 *   <LoginPage />
 * </GuestRoute>
 * ```
 */
export function GuestRoute({ children }: GuestRouteProps): JSX.Element {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isInitializing = useAuthStore((state) => state.isInitializing);
  const user = useAuthStore((state) => state.user);

  // Show loading while checking auth state
  if (isInitializing) {
    return <PageLoading />;
  }

  // Redirect authenticated users to their default route
  if (isAuthenticated && user) {
    const redirectTo = getDefaultRoute(user.role);
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

export default GuestRoute;
